import type { AssessmentSessionResponse } from "~/api/assessmentSessions"
import { shareCaptureAssetUrl } from "~/utils/assetUrl"
import { archetypeShareCaptureUrl, resolveArchetypeIcon } from "~/utils/archetypeImageUrl"
import { formatShareCardRarityLine, getArchetypePopulationPercent } from "~/utils/archetypeRarity"

interface ShareableCardImageProps {
  data: AssessmentSessionResponse
  aspectRatio?: "square" | "vertical"
  personalizedPortraitUrl?: string | null
}

export function ShareableCardImage({ data, aspectRatio = "vertical", personalizedPortraitUrl }: ShareableCardImageProps) {
  const archetypeName = data.archetype?.name ?? data.archetype_label ?? "The Archetype"
  const icon = resolveArchetypeIcon(data)
  const portraitSrc =
    personalizedPortraitUrl ??
    (icon ? archetypeShareCaptureUrl(icon, "portraits") : null)
  const populationPercent = data.archetype
    ? getArchetypePopulationPercent(data.archetype)
    : 5
  const rarityLine = formatShareCardRarityLine(populationPercent)
  const width = 1080
  const height = aspectRatio === "square" ? 1080 : 1350
  const innerBorderInset = 24
  const innerBorderStroke = 1
  const innerBorderRadius = 24

  const isVertical = aspectRatio === "vertical"
  const horizontalPadding = isVertical ? 56 : 64
  const portraitWidth = isVertical ? 520 : 420
  const portraitHeight = isVertical ? 325 : 240
  const sealSize = isVertical ? 120 : 96
  const emblemSize = isVertical ? 200 : 144

  return (
    <div
      data-shareable-image="true"
      style={{
        position: "relative",
        overflow: "hidden",
        boxSizing: "border-box",
        width: `${width}px`,
        height: `${height}px`,
        background: "radial-gradient(circle at 50% 0%, #f4e6d3 0%, #ecd8be 100%)",
        fontFamily: "Georgia, serif",
        color: "#4f3623",
      }}
    >
      <div style={{ position: "absolute", inset: 0 }}>
        <svg width={width} height={height} style={{ display: "block" }} aria-hidden>
          <rect
            x={innerBorderInset + innerBorderStroke / 2}
            y={innerBorderInset + innerBorderStroke / 2}
            width={width - innerBorderInset * 2 - innerBorderStroke}
            height={height - innerBorderInset * 2 - innerBorderStroke}
            rx={innerBorderRadius}
            ry={innerBorderRadius}
            fill="none"
            stroke="rgba(201,171,130,0.6)"
            strokeWidth={innerBorderStroke}
            shapeRendering="geometricPrecision"
          />
        </svg>
      </div>
      <div style={{ position: "absolute", inset: 0, opacity: 0.15 }}>
        <div
          style={{
            height: "100%",
            width: "100%",
            backgroundImage: "radial-gradient(circle,#c9ab82 1px,transparent 1px)",
            backgroundSize: "28px 28px",
          }}
        />
      </div>
      <div
        data-share-card-content="true"
        style={{
          position: "relative",
          zIndex: 10,
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-start",
          gap: isVertical ? "32px" : "24px",
          height: "100%",
          paddingTop: isVertical ? "70px" : "64px",
          paddingRight: `${horizontalPadding}px`,
          paddingBottom: isVertical ? "86px" : "72px",
          paddingLeft: `${horizontalPadding}px`,
          textAlign: "center",
        }}
      >
        <div data-share-card-main="true">
          <img
            src={shareCaptureAssetUrl("/images/logo.png")}
            alt="Doggy Dynamo"
            style={{
              display: "block",
              margin: "0 auto",
              height: "74px",
              width: "auto",
              objectFit: "contain",
            }}
          />

          <div
            style={{
              position: "relative",
              margin: `${isVertical ? "28px" : "20px"} auto 0`,
              width: `${portraitWidth}px`,
              maxWidth: "100%",
            }}
          >
            <div
              style={{
                borderRadius: "40px",
                border: "4px solid rgba(212,175,55,0.7)",
                background:
                  "linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(248,241,223,0.8) 50%, rgba(226,207,172,0.7) 100%)",
                padding: "4px",
                boxShadow: "0 28px 80px rgba(0,0,0,0.25)",
              }}
            >
              <div
                style={{
                  overflow: "hidden",
                  borderRadius: "36px",
                  border: "2px solid rgba(255,255,255,0.4)",
                  aspectRatio: "16/10",
                }}
              >
                <img
                  src={portraitSrc ?? undefined}
                  alt={`Portrait for ${archetypeName} archetype`}
                  style={{
                    display: "block",
                    width: "100%",
                    height: `${portraitHeight}px`,
                    objectFit: "cover",
                  }}
                />
              </div>
            </div>
            <div
              aria-hidden
              style={{
                position: "absolute",
                top: 0,
                right: 0,
                zIndex: 10,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: `${sealSize}px`,
                height: `${sealSize}px`,
                transform: "translate(33%, -33%)",
                borderRadius: "50%",
                border: "3px solid rgba(212,175,55,0.7)",
                background:
                  "radial-gradient(circle at 30% 20%, rgba(255,255,255,0.95), transparent 55%), rgba(255,248,224,0.7)",
                boxShadow: "0 18px 45px rgba(0,0,0,0.35)",
              }}
            >
              <img
                src={archetypeShareCaptureUrl(icon, "logos")}
                alt=""
                style={{
                  display: "block",
                  width: `${Math.round(sealSize * 0.55)}px`,
                  height: `${Math.round(sealSize * 0.55)}px`,
                  objectFit: "contain",
                }}
              />
            </div>
          </div>

          <p
            style={{
              margin: 0,
              marginTop: isVertical ? "32px" : "24px",
              fontSize: isVertical ? "52px" : "48px",
              lineHeight: 1.1,
            }}
          >
            {data.dog_name} is
          </p>
          <h1
            style={{
              margin: 0,
              marginTop: "8px",
              maxWidth: "100%",
              fontSize: isVertical ? "76px" : "68px",
              fontWeight: 700,
              lineHeight: 1.02,
              letterSpacing: "0.02em",
              textTransform: "uppercase",
              wordBreak: "break-word",
            }}
          >
            {archetypeName}
          </h1>

          <img
            src={archetypeShareCaptureUrl(icon, "logos")}
            alt={archetypeName}
            style={{
              display: "block",
              margin: `${isVertical ? "28px" : "20px"} auto 0`,
              height: `${emblemSize}px`,
              width: `${emblemSize}px`,
              objectFit: "contain",
              filter: "drop-shadow(0 8px 20px rgba(0,0,0,0.22))",
            }}
          />

          <p
            style={{
              margin: 0,
              marginTop: isVertical ? "32px" : "24px",
              fontSize: isVertical ? "46px" : "36px",
              lineHeight: 1.15,
              wordBreak: "break-word",
            }}
          >
            {data.archetype?.hook_primary ?? data.archetype_explanation ?? "Always figuring things out."}
          </p>
          <p
            data-share-card-rarity="true"
            style={{
              margin: 0,
              marginTop: isVertical ? "20px" : "16px",
              fontSize: isVertical ? "42px" : "32px",
              fontWeight: 500,
            }}
          >
            {rarityLine}
          </p>
        </div>

        <div
          data-share-card-footer="true"
          style={{
            flexShrink: 0,
            fontSize: isVertical ? "46px" : "28px",
            lineHeight: 1.2,
          }}
        >
          <p style={{ margin: 0 }}>Does this sound like your dog?</p>
          <p style={{ margin: 0, marginTop: "8px" }}>Discover your dog&apos;s personality | dynamo.dog</p>
        </div>
      </div>
    </div>
  )
}
